// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {getInventories} from 'Services/membership-service';
import {deleteBinLocation, getBinLocations} from 'Services/inventory-service';
import {fetchEffectData, useDeepEffect} from 'Utils/react-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import AddButton from 'Ui/components/settings/common/add-button';
import EditLink from 'Ui/components/common/data-table/edit-link';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {MANAGE_EQUIPMENT_SETTINGS} from 'Common/constants/business-activities';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

function getDataGridColumns(translations, inventoryId, openBinLocationDialog, fetchBinLocations, binLocations) {
    return [
        {
            headerName: translations.ONLINK_BIN_LOCATION,
            field: 'name',
            flex: 1,
            renderCell: (params) => {
                const {
                    row,
                    value
                } = params;

                return (
                    <EditLink
                        onClick={() => openBinLocationDialog({
                            binLocation: row,
                            inventoryBinLocations: binLocations,
                            inventoryId,
                            translations,
                            fetchBinLocations
                        })}
                    >
                        {value}
                    </EditLink>
                );
            }
        }
    ];
}

function getFilterComponent(translations, inventoryId, openBinLocationDialog, fetchBinLocations, binLocations) {
    return (
        <div className='extra-table-options'>
            <AddButton
                label={replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                    '0': translations.ONLINK_BIN_LOCATION
                })}
                onClick={() => openBinLocationDialog({
                    inventoryId,
                    inventoryBinLocations: binLocations,
                    translations,
                    fetchBinLocations
                })}
            />
        </div>
    );
}

function BinLocationsSettings(props) {
    const {
        closeConfirmation,
        openBinLocationDialog,
        openConfirmation,
        translations
    } = props;

    const {addToast} = useToast();
    const [loading, setLoading] = React.useState(true);
    const [binLocations, setBinLocations] = React.useState([]);
    const [inventoryId, setInventoryId] = React.useState(null);

    async function fetchBinLocations(isMounted = () => true) {
        setLoading(true);

        const {inventories} = await getInventories();
        const inventoryId = inventories?.length ? inventories[0].inventoryId : null;
        const {binLocations} = inventoryId ? await getBinLocations(inventoryId) : {
            binLocations: []
        };

        if (isMounted()) {
            setBinLocations(binLocations);
            setInventoryId(inventoryId);
            setLoading(false);
        }
    }

    function deleteBinLocationHandler(binLocation) {
        openConfirmation({
            message: replaceTranslationNames(translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE, {
                '0': translations.ONLINK_BIN_LOCATION.toLowerCase()
            }),
            onCancel: closeConfirmation,
            showLoadingIcon: true,
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.ONLINK_BIN_LOCATION
            }),
            async onContinue() {
                try {
                    await deleteBinLocation(binLocation.binLocationId);
                } catch {
                    addToast({
                        message: replaceTranslationNames(translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED, {
                            '0': translations.ONLINK_BIN_LOCATION.toLowerCase()
                        }),
                        type: TOAST_TYPE.ERROR
                    });
                }

                closeConfirmation();
                fetchBinLocations();
            }
        });
    }

    useDeepEffect(() => fetchEffectData(fetchBinLocations), []);

    return (
        <div
            className={'settings-container data-grid'}
            data-testid='settings-container'
        >
            <DataGridWrapper
                columns={getDataGridColumns(translations, inventoryId, openBinLocationDialog, fetchBinLocations, binLocations)}
                deleteHandler={deleteBinLocationHandler}
                filterComponent={getFilterComponent(translations, inventoryId, openBinLocationDialog, fetchBinLocations, binLocations)}
                getRowId={(row) => row.binLocationId}
                initialState={{
                    sorting: {
                        sortModel: [
                            {
                                field: 'name',
                                sort: 'asc'
                            }
                        ]
                    }
                }}
                loading={loading}
                noDataAvailableMessage={translations.ONLINK_NO_BIN_LOCATIONS_FOUND}
                rows={binLocations}
                searchFields={['name']}
                searchable={true}
                showDeleteColumn={isAuthorized({
                    myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                }, props)}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

BinLocationsSettings.propTypes = {
    closeConfirmation: PropTypes.func,
    isMigrated: PropTypes.bool,
    myJdPermissions: PropTypes.myJdPermissions,
    openBinLocationDialog: PropTypes.func,
    openConfirmation: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions,
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openBinLocationDialog(props) {
            dispatch(openDialog(dialogTypes.BIN_LOCATION_DIALOG, props));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(BinLocationsSettings);
